import { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  TrendingUp, 
  DollarSign, 
  BarChart3,
  PieChart as PieChartIcon,
  ArrowUpRight,
  Building2,
  Plane,
  Gem
} from 'lucide-react';
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  Tooltip, 
  ResponsiveContainer, 
  BarChart, 
  Bar,
  LineChart,
  Line,
  ComposedChart,
  Legend
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const VisualShockModule = ({ token }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedFranchise, setSelectedFranchise] = useState('all');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${API}/finance/dashboard`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setDashboardData(response.data);
      } catch (error) {
        console.error('Failed to fetch finance data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-[#DC143C] font-mono text-sm animate-pulse">
          CHARGEMENT DES DONNÉES FINANCIÈRES...
        </div>
      </div>
    );
  }

  const franchiseIcons = {
    immo: Building2,
    voyage: Plane,
    jade: Gem
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-[#0A0A0A] border border-[#DC143C]/50 p-3 min-w-[150px]">
          <p className="font-mono text-xs text-[#888888] mb-2">{label}</p>
          {payload.map((entry, index) => (
            <div key={index} className="flex items-center justify-between gap-4">
              <span className="font-mono text-xs" style={{ color: entry.color }}>
                {entry.name}
              </span>
              <span className="font-mono text-sm text-[#EDEDED]">
                €{entry.value?.toLocaleString()}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div data-testid="visual-shock-module" className="space-y-6 animate-fade-in">
      {/* Header Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="stat-card card-hover border-l-2 border-l-[#DC143C]">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-[#DC143C]/10 border border-[#DC143C]/50">
              <DollarSign className="w-5 h-5 text-[#DC143C]" />
            </div>
            <span className="font-heading text-xs uppercase tracking-widest text-[#888888]">
              Total Mensuel
            </span>
          </div>
          <p className="font-mono text-3xl font-bold text-[#EDEDED] glow-text">
            €{(dashboardData?.total_monthly || 0).toLocaleString()}
          </p>
        </div>

        <div className="stat-card card-hover border-l-2 border-l-[#00FF9D]">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-[#00FF9D]/10 border border-[#00FF9D]/50">
              <TrendingUp className="w-5 h-5 text-[#00FF9D]" />
            </div>
            <span className="font-heading text-xs uppercase tracking-widest text-[#888888]">
              Projection Annuelle
            </span>
          </div>
          <p className="font-mono text-3xl font-bold text-[#EDEDED]">
            €{(dashboardData?.total_yearly || 0).toLocaleString()}
          </p>
        </div>

        <div className="stat-card card-hover border-l-2 border-l-[#FFD700]">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-[#FFD700]/10 border border-[#FFD700]/50">
              <ArrowUpRight className="w-5 h-5 text-[#FFD700]" />
            </div>
            <span className="font-heading text-xs uppercase tracking-widest text-[#888888]">
              Croissance Moyenne
            </span>
          </div>
          <p className="font-mono text-3xl font-bold text-[#EDEDED]">
            +{dashboardData?.avg_growth || 0}%
          </p>
        </div>
      </div>

      {/* Main Chart - Cascade de Revenus */}
      <div className="grid-card">
        <div className="module-header">
          <div className="flex items-center gap-3">
            <BarChart3 className="w-4 h-4 text-[#DC143C]" />
            <span className="module-title">CASCADE DE REVENUS - CHOC VISUEL</span>
          </div>
          <div className="flex gap-2">
            <button 
              onClick={() => setSelectedFranchise('all')}
              className={`module-badge cursor-pointer ${selectedFranchise === 'all' ? 'bg-[#DC143C]/40' : ''}`}
            >
              TOUTES
            </button>
          </div>
        </div>
        <div className="p-6 h-80">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={dashboardData?.cascade_data || []}>
              <defs>
                <linearGradient id="colorCumulative" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#DC143C" stopOpacity={0.4}/>
                  <stop offset="95%" stopColor="#DC143C" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <XAxis 
                dataKey="month" 
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#888888', fontSize: 11, fontFamily: 'JetBrains Mono' }}
              />
              <YAxis 
                yAxisId="left"
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#888888', fontSize: 11, fontFamily: 'JetBrains Mono' }}
                tickFormatter={(value) => `€${(value/1000).toFixed(0)}k`}
              />
              <YAxis 
                yAxisId="right"
                orientation="right"
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#888888', fontSize: 11, fontFamily: 'JetBrains Mono' }}
                tickFormatter={(value) => `€${(value/1000000).toFixed(1)}M`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                wrapperStyle={{ 
                  paddingTop: '20px',
                  fontFamily: 'JetBrains Mono',
                  fontSize: '11px'
                }}
              />
              <Bar yAxisId="left" dataKey="immo" name="Immobilier" fill="#DC143C" />
              <Bar yAxisId="left" dataKey="voyage" name="Voyage" fill="#00FF9D" />
              <Bar yAxisId="left" dataKey="jade" name="Jade" fill="#FFD700" />
              <Line 
                yAxisId="right"
                type="monotone" 
                dataKey="cumulative" 
                name="Cumulatif"
                stroke="#00BFFF" 
                strokeWidth={3}
                dot={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Franchise Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {dashboardData?.franchises?.map((franchise, index) => {
          const Icon = franchiseIcons[franchise.type] || Building2;
          return (
            <div 
              key={franchise.id || index}
              className="grid-card card-hover"
              style={{ borderTopColor: franchise.color, borderTopWidth: '2px' }}
            >
              <div className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div 
                      className="p-2 border"
                      style={{ 
                        backgroundColor: `${franchise.color}10`,
                        borderColor: `${franchise.color}50`
                      }}
                    >
                      <Icon className="w-5 h-5" style={{ color: franchise.color }} />
                    </div>
                    <div>
                      <h3 className="font-heading text-sm font-bold text-[#EDEDED] uppercase">
                        {franchise.name.replace('DNCM ', '')}
                      </h3>
                      <p className="font-mono text-[10px] text-[#888888]">
                        {franchise.type.toUpperCase()}
                      </p>
                    </div>
                  </div>
                  <span 
                    className="font-mono text-xs px-2 py-1 border"
                    style={{ 
                      color: franchise.color,
                      borderColor: `${franchise.color}50`,
                      backgroundColor: `${franchise.color}10`
                    }}
                  >
                    +{franchise.growth_rate}%
                  </span>
                </div>

                <div className="space-y-3">
                  <div>
                    <p className="font-mono text-xs text-[#888888] mb-1">MENSUEL</p>
                    <p className="font-mono text-xl font-bold text-[#EDEDED]">
                      €{franchise.monthly_revenue?.toLocaleString()}
                    </p>
                  </div>
                  <div className="progress-bar">
                    <div 
                      className="progress-fill"
                      style={{ 
                        width: `${(franchise.monthly_revenue / (dashboardData?.total_monthly || 1)) * 100}%`,
                        backgroundColor: franchise.color
                      }}
                    />
                  </div>
                  <div className="flex justify-between">
                    <p className="font-mono text-xs text-[#888888]">
                      Projection: €{franchise.yearly_projection?.toLocaleString()}
                    </p>
                    <p className="font-mono text-xs" style={{ color: franchise.color }}>
                      {((franchise.monthly_revenue / (dashboardData?.total_monthly || 1)) * 100).toFixed(1)}%
                    </p>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Growth Projection Chart */}
      <div className="grid-card">
        <div className="module-header">
          <div className="flex items-center gap-3">
            <PieChartIcon className="w-4 h-4 text-[#00FF9D]" />
            <span className="module-title">PROJECTION DE CROISSANCE PAR FRANCHISE</span>
          </div>
          <span className="module-badge">12 MOIS</span>
        </div>
        <div className="p-6 h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={dashboardData?.cascade_data || []}>
              <defs>
                <linearGradient id="colorImmo" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#DC143C" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#DC143C" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="colorVoyage" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#00FF9D" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#00FF9D" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="colorJade" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#FFD700" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#FFD700" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <XAxis 
                dataKey="month" 
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#888888', fontSize: 10, fontFamily: 'JetBrains Mono' }}
              />
              <YAxis 
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#888888', fontSize: 10, fontFamily: 'JetBrains Mono' }}
                tickFormatter={(value) => `€${(value/1000).toFixed(0)}k`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area 
                type="monotone" 
                dataKey="immo" 
                name="Immobilier"
                stroke="#DC143C" 
                strokeWidth={2}
                fillOpacity={1}
                fill="url(#colorImmo)"
                stackId="1"
              />
              <Area 
                type="monotone" 
                dataKey="voyage" 
                name="Voyage"
                stroke="#00FF9D" 
                strokeWidth={2}
                fillOpacity={1}
                fill="url(#colorVoyage)"
                stackId="1"
              />
              <Area 
                type="monotone" 
                dataKey="jade" 
                name="Jade"
                stroke="#FFD700" 
                strokeWidth={2}
                fillOpacity={1}
                fill="url(#colorJade)"
                stackId="1"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default VisualShockModule;

